import corner3 from 'assets/img/illustrations/corner-3.png';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  FormLabel,
  InputGroup,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SimpleBarReact from 'simplebar-react';
import { faStar, faEye } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import api from 'util/api';
import enums from 'util/enums';
import logger from 'util/logger';
import draftEntryUiHelper from 'helpers/draft-entry-ui-helper';
import { showMasterSignerDialog } from 'stores/modal';
import { useDispatch } from 'react-redux';
import { actionReset } from 'stores/signing';
import { useAsyncDebounce } from 'react-table';
import IconAlert from 'components/common/IconAlert';
import { useListArchivedSignersQuery } from 'rtk/signer.slice';

const ArchivedClientList = () => {
  const [show, setShow] = useState(true);
  const [sortBy, setSortBy] = useState('Created');
  const [isAsc, setIsAsc] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const skip = pageIndex * pageSize;

  const {
    data: archivedRes,
    isFetching,
    isLoading,
    isError: archivedLoadError,
    error: archivedError,
    refetch: refetchArchivedSigners
  } = useListArchivedSignersQuery({
    skip,
    pageSize,
    keyword: searchKeyword,
    sortKey: sortBy,
    sortOrderAsc: isAsc
  });
  const loading = isFetching || isLoading;

  const archivedObjects = archivedRes?.Objects || [];
  const totalRecords = archivedRes?.TotalRecords ?? 0;
  const endOfResults = !!archivedRes?.EndOfResults;
  const dispatch = useDispatch();

  const loadMasterSiners = (...args) => {
    const [start = false, keyword, sortByParam, sortOrder] = args;
    const kw =
      keyword !== undefined && keyword !== null ? keyword : searchKeyword;
    const sb =
      sortByParam !== undefined && sortByParam !== null ? sortByParam : sortBy;
    const asc =
      sortOrder !== undefined && sortOrder !== null ? sortOrder : isAsc;

    if (start === false) {
      setPageIndex(0);
    }
    if (start === true) {
      setPageIndex(prev => prev + 1);
    }
    if (kw !== searchKeyword) setSearchKeyword(kw);
    if (sb !== sortBy) setSortBy(sb);
    if (asc !== isAsc) setIsAsc(asc);
  };

  useEffect(() => {
    if (archivedLoadError) {
      logger.log('error loading archived clients', archivedError);
    }
  }, [archivedLoadError, archivedError]);

  useEffect(() => {
    dispatch(actionReset());
    refetchArchivedSigners();
  }, []);

  useEffect(() => {
    setPageIndex(0);
    // RTK Query refetches automatically when pageSize changes
  }, [pageSize]);

  // Search Master Client
  const searchItems = useAsyncDebounce(e => {
    let keyword = e.target.value.toLowerCase().trim();
    setSearchKeyword(keyword);
    loadMasterSiners(false, keyword, sortBy, isAsc);
  }, 500);

  // Sort Master Client
  const sortItems = (sortValue, isAsc) => {
    setSortBy(sortValue);
    setIsAsc(isAsc);
    loadMasterSiners(false, searchKeyword, sortValue, isAsc);
  };

  // view each master client record
  const onRowClicked = entry => {
    dispatch(
      showMasterSignerDialog(entry.GUID, 'archivedMaster', res => {
        if (res) {
          // initializeEntries();
        }
      })
    );
  };

  const renderIdentificationsTag = e => {
    if (e?.IdentificationTypes?.length == 0) {
      return 'NA';
    }
    return (
      <>
        {e.IdentificationTypes.map((item, index) =>
          item !== enums.ID_TYPES.NONE ? (
            <span
              key={index}
              className="ms-2 fs--2 badge badge-soft-success rounded-pill"
            >
              {item}
            </span>
          ) : null
        )}
      </>
    );
  };

  // eslint-disable-next-line react/prop-types
  const IconAlertCode = function IconAlertDemo({ variant, message }) {
    if (!show) return null;
    return (
      <IconAlert variant={variant} dismissible onClose={() => setShow(false)}>
        <p className="mb-0">{message}</p>
      </IconAlert>
    );
  };

  return (
    <>
      {/* <PageHeader
        preTitle="Archived Clients"
        title="Archived Clients"
        description=""
        className="mb-3"
        titleTag="h2"
        image={corner3}
      /> */}

      <IconAlertCode
        variant="info"
        message="Your deleted and merged clients are archived here. You can view them here, but they are not available for use in new journal entries."
      />

      <Row className="mb-3 g-3">
        <Col lg={12}>
          <FalconComponentCard noGuttersBottom>
            <FalconComponentCard.Header
              image={corner3}
              className={'px-4'}
              title="Archived Clients"
              subtitle="Browse your archived clients"
              endEl={
                <Flex className={'g-2'}>
                  <div>
                    <Form as={Row} className="gx-2 d-flex align-items-center">
                      <Col xs="auto">
                        <FormLabel className="mb-0">Sort by:</FormLabel>
                      </Col>
                      <Col xs="auto">
                        <InputGroup>
                          <Form.Select
                            size="sm"
                            className="pe-5"
                            defaultValue="Created"
                            onChange={({ target }) =>
                              sortItems(target.value, isAsc)
                            }
                          >
                            <option value="Created">Created</option>
                            <option value="IsFavorite">Colleagues</option>
                            <option value="FirstName">Client Name</option>
                            {/* <option value="JournalEntries">
                              Journal Entries
                            </option>
                            <option value="PotentialDuplicates">
                              Potential Duplicate
                            </option> */}
                          </Form.Select>
                          <InputGroup.Text
                            as={Button}
                            variant="link"
                            className="border border-300 text-700"
                            onClick={() => sortItems(sortBy, !isAsc)}
                          >
                            <FontAwesomeIcon
                              icon={
                                isAsc ? 'sort-amount-up' : 'sort-amount-down'
                              }
                            />
                          </InputGroup.Text>
                        </InputGroup>
                      </Col>
                    </Form>
                  </div>
                  <div className="mx-1">
                    <Form.Control
                      onChange={e => searchItems(e)}
                      placeholder="Search Client List"
                      name="search"
                    ></Form.Control>
                  </div>
                </Flex>
              }
            />
            {/* table display */}
            <FalconComponentCard.Body noLight className="py-0 px-4">
              {isLoading && (
                <Flex justifyContent="center" className="py-3">
                  <Spinner size="sm" />
                </Flex>
              )}
              {archivedObjects.length > 0 && !isLoading && (
                <Pagination
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  currentCount={archivedObjects.length}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  endOfResults={endOfResults}
                  disabled={loading}
                  onRefresh={() => refetchArchivedSigners()}
                  loading={loading}
                />
              )}
              {archivedObjects.length !== 0 &&
                !archivedLoadError &&
                !isLoading && (
                  <SimpleBarReact>
                    <Table
                      striped
                      bordered
                      className={loading ? 'refreshing-skeleton' : undefined}
                    >
                      <colgroup>
                        <col />
                        <col />
                        <col className="d-sm-none d-md-block" />
                        <col />
                      </colgroup>
                      <thead>
                        <tr>
                          <th scope="col">Colleagues</th>
                          <th scope="col">Client Name</th>
                          <th scope="col">Total Identifications </th>
                          <th scope="col">ID Types </th>
                          <th scope="col">Journals entries</th>
                          <th scope="col">Potential Duplicates</th>
                          <th scope="col">Created</th>
                          <th scope="col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {archivedObjects.map(e => {
                          let createdDate = draftEntryUiHelper
                            .getDateTimeWithTimeZone(e.Created)
                            .format('l, LT z');
                          return (
                            <tr key={e.id}>
                              <td>
                                <FontAwesomeIcon
                                  className={e.IsFavorite ? 'text-primary' : ''}
                                  icon={e.IsFavorite ? faStar : faStarRegular}
                                />
                              </td>
                              <td
                                className="fw-bold cursor-pointer"
                                onClick={() => onRowClicked(e)}
                              >
                                {e.Prefix && e.Prefix + ' '}
                                {e.FirstName}{' '}
                                {e.MiddleName && ' ' + e.MiddleName + ' '}{' '}
                                {e.LastName} {e.Suffix}
                              </td>
                              <td>{e.IdentificationCount}</td>
                              <td>{renderIdentificationsTag(e)}</td>
                              <td>{e.SigningCount || 0}</td>
                              <td>{e.PotentialDuplicateSignerCount || 0}</td>
                              <td>{createdDate}</td>
                              <td className="cursor-pointer d-flex gap-2">
                                <Button
                                  variant="outline-dark"
                                  size="sm"
                                  title="View"
                                  label="View"
                                  onClick={() => onRowClicked(e)}
                                >
                                  {' '}
                                  <FontAwesomeIcon icon={faEye} />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </SimpleBarReact>
                )}
              {archivedObjects.length === 0 && !loading && (
                <p className="my-3 text-center">You have no client data.</p>
              )}
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>
    </>
  );
};
export default ArchivedClientList;
